import { useMemo, useCallback } from "react";
import { TokenService } from "../services/tokenService";

export interface UsePermissionsReturn {
  hasPermission: (
    screenName: string,
    action: "read" | "create" | "update" | "delete"
  ) => boolean;
  getScreenPermissions: (screenName: string) => {
    read: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  } | null;
  canAccess: (
    screenName: string,
    action: "read" | "create" | "update" | "delete"
  ) => boolean;
  canRead: (screenName: string) => boolean;
  canCreate: (screenName: string) => boolean;
  canUpdate: (screenName: string) => boolean;
  canDelete: (screenName: string) => boolean;
  isAuthenticated: boolean;
}

/**
 * Custom hook for managing user permissions across multiple screens
 * Uses the stored JWT token to determine what actions the user can perform
 */
export const usePermissions = (): UsePermissionsReturn => {
  const token = useMemo(
    () =>
      localStorage.getItem("bestekJwtToken") ||
      sessionStorage.getItem("bestekJwtToken"),
    []
  );

  const hasPermission = useCallback(
    (
      screenName: string,
      action: "read" | "create" | "update" | "delete"
    ): boolean => {
      if (!token) {
        return false;
      }

      return TokenService.hasPermission(token, screenName, action);
    },
    [token]
  );

  const getScreenPermissions = useCallback(
    (screenName: string) => {
      if (!token) {
        return null;
      }
      return TokenService.getScreenPermissions(token, screenName);
    },
    [token]
  );

  // Generic permission check functions that can be used for any screen
  const canAccess = useCallback(
    (
      screenName: string,
      action: "read" | "create" | "update" | "delete"
    ): boolean => {
      return hasPermission(screenName, action);
    },
    [hasPermission]
  );

  const canRead = useCallback(
    (screenName: string): boolean => {
      return hasPermission(screenName, "read");
    },
    [hasPermission]
  );

  const canCreate = useCallback(
    (screenName: string): boolean => {
      return hasPermission(screenName, "create");
    },
    [hasPermission]
  );

  const canUpdate = useCallback(
    (screenName: string): boolean => {
      return hasPermission(screenName, "update");
    },
    [hasPermission]
  );

  const canDelete = useCallback(
    (screenName: string): boolean => {
      return hasPermission(screenName, "delete");
    },
    [hasPermission]
  );

  const isAuthenticated = useMemo(() => {
    if (!token) return false;
    const userInfo = TokenService.getUserInfoFromToken(token);
    return userInfo?.isAuthenticated ?? false;
  }, [token]);

  return {
    hasPermission,
    getScreenPermissions,
    canAccess,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    isAuthenticated,
  };
};
